import { IStyle } from "./../Types/Style";
import { RouterState } from "connected-react-router";
import { Action, combineReducers } from "redux";
import { Action as ReduxAction, handleActions, ReducerMap } from "redux-actions";
import { IAction } from "reduxsauce";

import { Types as AllTypes } from "../actions";
import { LoadAction } from "../actions/load-amounts";
import { Action as OrgAction } from "../actions/types";
import { INITIAL_OBJECT_AMOUNT, ObjectAmount } from "../App/actions";
import { ICourseLocalizedText, ICourseSet, IPublicationInfo } from "../components/course-author/types";
import { courseAuthorReducer } from "../reducers/course-author/course-author";
import { IState as ICourseAuthorState } from "../reducers/course-author/types";
import dashboardReducer, { IState as IDashboardState } from "../reducers/dashboard";
import { PreferredLocale } from "../utils/locales";
import accountReducer, { IState as IAccountState } from "./Account/reducers";
import { IAmountAction, IObjectsAmount } from "./actions";
import classAnalyticsReducer, { IState as IClassAnalyticsState } from "./Analytics/ClassAnalytics/reducer";
import scormPackageAnalyticsReducer, {
  IState as IScormPackageAnalyticsState,
} from "./Analytics/ScormPackageAnalytics/reducer";
import classesReducer, { IState as IClassesState } from "./Classes/reducer";
import { classManagementReducer, IState as IClassManagementState } from "./ClassManagement/reducer";
import coursesReducer, { IState as ICoursesState } from "./Courses/reducer";
import exportedScormPackagesReducer, { IState as IExportedScormPackages } from "./ExportedScormPackages/reducer";
import organizationSettingsReducer, { IOrganizationSettingsState } from "./Organization/OrganizationSettings/reducers";
import scormPackageManagementReducer, {
  IState as IScormPackageManagementState,
} from "./ScormPackageManagement/reducers";
import selectOrganizationReducer, { IState as ISelectOrganizationState } from "./SelectOrganization/reducer";
import themeManagementReducer, { IState as IThemeManagementState } from "./ThemeManagement/reducers";
import themeListReducer, { IState as IThemeListState } from "./Themes/reducers";
import { ScormPackageState } from "./types/common";

export interface IState {
  App: {
    _: IAppState;
    Account: IAccountState;
    ClassAnalytics: IClassAnalyticsState;
    Classes: IClassesState;
    ClassManagement: IClassManagementState;
    Courses: ICoursesState;
    CourseAuthor: ICourseAuthorState;
    Dashboard: IDashboardState;
    ExportedScormPackages: IExportedScormPackages;
    SelectOrganization: ISelectOrganizationState;
    OrganizationSettings: IOrganizationSettingsState;
    ScormPackageManagement: IScormPackageManagementState;
    ScormPackageAnalytics: IScormPackageAnalyticsState;
    ThemeList: IThemeListState;
    ThemeManagement: IThemeManagementState;
  };
  router: RouterState;
}

export enum LoginRequestState {
  IDLE = "IDLE",
  PENDING = "PENDING",
  FAILED = "FAILED",
  SUCCEEDED = "SUCCEEDED",
}

export enum LogoutRequestState {
  IDLE = "IDLE",
  PENDING = "PENDING",
  FAILED = "FAILED",
  SUCCEEDED = "SUCCEEDED",
}

export interface IUser {
  accountId: string;
  displayName: string;
  emailAddresses: string[];
  ownerOrganizationId: string;
  preferredLocale: PreferredLocale;
  primaryEmailAddress: string | null;
  canAccessDashboard: boolean;
  canResetCourse: boolean;
  isAdmin: boolean;
  isAuthor: boolean;
  isLearner: boolean;
  isManager: boolean;
  isOrgAdmin: boolean;
}

export interface IOrganization {
  organizationId: string;
  ownerOrganizationId: string;
  displayName: string;
  defaultLocale: PreferredLocale | null;
  releaseLine: string;
  style: IStyle | null;
}

export interface ICourse {
  courseId: string;
  createdAt: string;
  displayName: string;
  locked: boolean;
  masterLocale: PreferredLocale;
  supportedLocales: PreferredLocale[];
  publicationInfo: IPublicationInfo | null;
  lastEditorName: string;
  latestRevId: number;
}

export interface IClassGoalResponse {
  id: number;
  course: string;
  displayName: string;
  courseInstEnv: {
    allowCourseReview: boolean;
    assumePriorKnowledge: boolean;
    includeBranding: boolean;
    useStrictMode: boolean;
  };
  deleted: boolean;
  description: string;
}

export interface IClass {
  admins: string[];
  classId: string;
  displayName: string;
  courseIds: string[];
  goals: {
    elems: IClassGoalResponse[];
    nextId: number;
  };
  members: string[];
  versionId: number;
}

export interface ICourseInstance {
  courseInstanceId: string;
  courseId: string;
}

export interface IScormPackage {
  scormPackageId: string;
  courseId: string;
  completedExitText: ICourseSet<ICourseLocalizedText>;
  expectedNumUsers: number | null;
  hideLmsNavbar: boolean;
  inProgressExitText: ICourseSet<ICourseLocalizedText>;
  name: string;
  lms: string;
  scormVersion: string;
  state: ScormPackageState;
  versionId: number;
  useCustomCompletedExitText: boolean;
  useCustomInProgressExitText: boolean;
  includeBranding: boolean;
  allowCourseReview: boolean;
  useStrictMode: boolean;
  locked: boolean;
}

export interface IFontFamily {
  ffmId: string;
  displayName: string;
}

export interface ISkinInfo {
  version: string | null;
  root: string | null;
}

export interface IAuthenticationState {
  canAccessDashboard: boolean;
  isAuthenticated: boolean;
  isKnown: boolean;
}

export interface IAppState {
  administrableOrgIds: string[];
  loginRequestState: LoginRequestState;
  loginRequestError: string | null;
  logoutRequestState: LogoutRequestState;
  logoutRequestError: string | null;
  user: IUser | null;
  organization: IOrganization | null;
  authenticationState: IAuthenticationState;
  skin: ISkinInfo | null;
  objectsAmount: IObjectsAmount;
}

const Types = (AllTypes as any)._APP;
const SelectOrganizationTypes = (AllTypes as any)._APP._SELECT_ORGANIZATION;

const INITIAL_OBJECT_AMOUNTS = {
  classAmount: INITIAL_OBJECT_AMOUNT,
  courseAmount: INITIAL_OBJECT_AMOUNT,
  exportedScormPackagesAmount: INITIAL_OBJECT_AMOUNT,
  groupsAmount: INITIAL_OBJECT_AMOUNT,
  usersAmount: INITIAL_OBJECT_AMOUNT,
};

export const INITIAL_STATE: IAppState = {
  administrableOrgIds: [],
  authenticationState: {
    canAccessDashboard: false,
    isAuthenticated: false,
    isKnown: false,
  },
  loginRequestError: null,
  loginRequestState: LoginRequestState.IDLE,
  logoutRequestError: null,
  logoutRequestState: LogoutRequestState.IDLE,
  objectsAmount: INITIAL_OBJECT_AMOUNTS,
  organization: null,
  skin: null,
  user: null,
};

export function mount(state: IAppState, action: IAction): IAppState {
  return INITIAL_STATE;
}

export const handleFetchLocalAuthenticationSuccess = (state: IAppState, action: IAction): IAppState => ({
  ...state,
  authenticationState: action.authenticationState,
  objectsAmount: action.objectsAmount,
  organization: action.organization,
  user: action.user,
});

export function login(state: IAppState, action: IAction): IAppState {
  return {
    ...state,
    loginRequestError: null,
    loginRequestState: LoginRequestState.PENDING,
  };
}

export function handleLoginFailure(state: IAppState, action: IAction): IAppState {
  return {
    ...state,
    authenticationState: {
      canAccessDashboard: false,
      isAuthenticated: false,
      isKnown: true,
    },
    loginRequestError: action.error.message,
    loginRequestState: LoginRequestState.FAILED,
  };
}

export function handleLoginSuccess(state: IAppState, action: IAction): IAppState {
  return {
    ...state,
    authenticationState: {
      canAccessDashboard: action.user.isAdmin || action.user.canAccessDashboard,
      isAuthenticated: true,
      isKnown: true,
    },
    loginRequestState: LoginRequestState.SUCCEEDED,
    organization: action.organization,
    skin: {
      root: action.root,
      version: action.version,
    },
    user: action.user,
  };
}

export function logout(state: IAppState, action: IAction) {
  return {
    ...state,
    logoutRequestError: null,
    logoutRequestState: LogoutRequestState.PENDING,
  };
}

export function handleLogoutSuccess(state: IAppState, action: IAction) {
  return {
    ...state,
    authenticationState: {
      canAccessDashboard: false,
      isAuthenticated: false,
      isKnown: true,
    },
    logoutRequestState: LogoutRequestState.SUCCEEDED,
  };
}

export function handleSelectOrganizationSuccess(state: IAppState, action: IAction): IAppState {
  return {
    ...state,
    objectsAmount: INITIAL_OBJECT_AMOUNTS,
    organization: action.organization,
  };
}

export function handleSelectOrganizationFailure(state: IAppState, action: IAction): IAppState {
  return {
    ...state,
    objectsAmount: INITIAL_OBJECT_AMOUNTS,
  };
}

export const handleLoadClassAmount = (state: IAppState, action: IAmountAction): IAppState => ({
  ...state,
  objectsAmount: { ...state.objectsAmount, classAmount: action.amount },
});

export const handleLoadCourseAmount = (state: IAppState, action: IAmountAction): IAppState => ({
  ...state,
  objectsAmount: { ...state.objectsAmount, courseAmount: action.amount },
});

export const handleLoadGroupsAmount = (state: IAppState, action: IAmountAction): IAppState => ({
  ...state,
  objectsAmount: { ...state.objectsAmount, groupsAmount: action.amount },
});

export const handleLoadUsersAmount = (state: IAppState, action: IAmountAction): IAppState => ({
  ...state,
  objectsAmount: { ...state.objectsAmount, usersAmount: action.amount },
});

export const handleLoadExportedSCORMPackagesAmount = (
  state: IAppState,
  action: ReduxAction<ObjectAmount>
): IAppState => ({
  ...state,
  objectsAmount: {
    ...state.objectsAmount,
    exportedScormPackagesAmount: action.payload,
  },
});

const handleLoadAdministrableOrdIdsSuccess = (state: IAppState, action: ReduxAction<string[]>): IAppState => ({
  ...state,
  administrableOrgIds: action.payload,
});

const handleLoadAdministrableOrdIdsFailure = (state: IAppState): IAppState => ({
  ...state,
  administrableOrgIds: [],
});

export const handleUpdateCoursesAmount = (state: IAppState, action: IAction): IAppState => ({
  ...state,
  objectsAmount: {
    ...state.objectsAmount,
    courseAmount: parseInt(state.objectsAmount.courseAmount as string, 0) + action.amount,
  },
});

export function unmount(state: IAppState, action: IAction): IAppState {
  return INITIAL_STATE;
}

const reduxactionsReducers: ReducerMap<IAppState, ObjectAmount | string[] | void> = {
  [LoadAction.HANDLE_LOAD_EXPORTED_SCORM_PACKAGES_AMOUNT]: handleLoadExportedSCORMPackagesAmount,
  [OrgAction.HANDLE_LOAD_ADMINISTRABLE_ORG_IDS_SUCCESS]: handleLoadAdministrableOrdIdsSuccess,
  [OrgAction.HANDLE_LOAD_ADMINISTRABLE_ORG_IDS_FAILURE]: handleLoadAdministrableOrdIdsFailure,
};

const saucesReducers: ReducerMap<IAppState, any> = {
  [Types.MOUNT]: mount,
  [Types.HANDLE_FETCH_LOCAL_AUTHENTICATION_SUCCESS]: handleFetchLocalAuthenticationSuccess,
  [Types.LOGIN]: login,
  [Types.LOGOUT]: logout,
  [SelectOrganizationTypes.HANDLE_LOAD_CLASS_AMOUNT]: handleLoadClassAmount,
  [SelectOrganizationTypes.HANDLE_LOAD_COURSE_AMOUNT]: handleLoadCourseAmount,
  [SelectOrganizationTypes.HANDLE_LOAD_GROUPS_AMOUNT]: handleLoadGroupsAmount,
  [SelectOrganizationTypes.HANDLE_LOAD_USERS_AMOUNT]: handleLoadUsersAmount,
  [SelectOrganizationTypes.HANDLE_UPDATE_COURSES_AMOUNT]: handleUpdateCoursesAmount,
  [Types.HANDLE_LOGIN_SUCCESS]: handleLoginSuccess,
  [Types.HANDLE_LOGIN_FAILURE]: handleLoginFailure,
  [Types.HANDLE_LOGOUT_SUCCESS]: handleLogoutSuccess,
  [SelectOrganizationTypes.HANDLE_SELECT_ORGANIZATION_SUCCESS]: handleSelectOrganizationSuccess,
  [SelectOrganizationTypes.HANDLE_SELECT_ORGANIZATION_FAILURE]: handleSelectOrganizationFailure,
  [Types.UNMOUNT]: unmount,
};

export const appReducer = combineReducers<IState["App"], Action>({
  Account: accountReducer,
  ClassAnalytics: classAnalyticsReducer,
  ClassManagement: classManagementReducer,
  Classes: classesReducer,
  CourseAuthor: courseAuthorReducer,
  Courses: coursesReducer,
  Dashboard: dashboardReducer,
  ExportedScormPackages: exportedScormPackagesReducer,
  OrganizationSettings: organizationSettingsReducer,
  ScormPackageAnalytics: scormPackageAnalyticsReducer,
  ScormPackageManagement: scormPackageManagementReducer,
  SelectOrganization: selectOrganizationReducer,
  ThemeList: themeListReducer,
  ThemeManagement: themeManagementReducer,
  _: handleActions(
    {
      ...reduxactionsReducers,
      ...saucesReducers,
    },
    INITIAL_STATE
  ),
});
